import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Alert,
  IconButton,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Button,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import PaletteIcon from '@mui/icons-material/Palette';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { format, addDays, startOfDay } from 'date-fns';
import { da } from 'date-fns/locale';
import type { TrailerCalendarData, CalendarCargoEntry, TeamInfo } from '../../types';

interface CargoCalendarProps {
  trailers: TrailerCalendarData[];
  teams: TeamInfo[];
  isLoading?: boolean;
  error?: Error | null;
  periodDays: number;
  onPeriodChange: (days: number) => void;
  startDate: Date;
  cargoTypeColors?: Record<string, string>;
  onUpdateColors?: (colors: Record<string, string>) => Promise<void>;
  onResetColors?: () => Promise<void>;
  isUpdatingColors?: boolean;
}

const PERIOD_OPTIONS = [
  { value: 7, label: '7 dage' },
  { value: 14, label: '14 dage' },
  { value: 30, label: '30 dage' },
];

const STORAGE_KEY = 'dora-calendar-filters';

// Default color map for cargo types
const DEFAULT_CARGO_TYPE_COLORS: Record<string, string> = {
  PALLET_GOODS: '#4caf50',
  GENERAL_CARGO: '#2196f3',
  COOL: '#00bcd4',
  FREEZE: '#03a9f4',
  BULK_CARGO: '#ff9800',
  CONTAINER: '#9c27b0',
  MACHINES: '#795548',
  MISCELLANEOUS: '#607d8b',
  DEFAULT: '#9e9e9e',
};

// Predefined color palette for easy selection
const COLOR_PALETTE = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7',
  '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
  '#009688', '#4caf50', '#8bc34a', '#cddc39',
  '#ffeb3b', '#ffc107', '#ff9800', '#ff5722',
  '#795548', '#9e9e9e', '#607d8b', '#000000',
];

interface FilterState {
  hiddenTrailers: string[];
  hiddenTeams: string[];
}

function getCargoColor(type: string, customColors?: Record<string, string>): string {
  if (customColors && customColors[type]) {
    return customColors[type];
  }
  return DEFAULT_CARGO_TYPE_COLORS[type] || DEFAULT_CARGO_TYPE_COLORS.DEFAULT;
}

function formatCargoType(type: string): string {
  const typeMap: Record<string, string> = {
    PALLET_GOODS: 'Pallegods',
    GENERAL_CARGO: 'Stykgods',
    COOL: 'Køl',
    FREEZE: 'Frost',
    BULK_CARGO: 'Bulkgods',
    CONTAINER: 'Container',
    MACHINES: 'Maskiner',
    MISCELLANEOUS: 'Diverse',
  };
  return typeMap[type] || type;
}

function loadFilters(): FilterState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return { hiddenTrailers: [], hiddenTeams: [] };
}

function saveFilters(filters: FilterState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore errors
  }
}

function CargoTooltipContent({ cargo }: { cargo: CalendarCargoEntry }) {
  const pickupLocation = [
    cargo.pickup.city,
    cargo.pickup.postcode,
    cargo.pickup.countryCode,
  ].filter(Boolean).join(', ');

  const deliveryLocation = cargo.delivery ? [
    cargo.delivery.city,
    cargo.delivery.postcode,
    cargo.delivery.countryCode,
  ].filter(Boolean).join(', ') : null;

  return (
    <Box sx={{ p: 1, maxWidth: 350 }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {cargo.ref}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2">
          <strong>Type:</strong> {formatCargoType(cargo.type)}
        </Typography>

        {cargo.description && (
          <Typography variant="body2">
            <strong>Beskrivelse:</strong> {cargo.description}
          </Typography>
        )}

        {(cargo.customerNumber || cargo.customerName) && (
          <Typography variant="body2">
            <strong>Kunde:</strong> {cargo.customerName || cargo.customerNumber}
          </Typography>
        )}

        <Typography variant="body2">
          <strong>Afhentning:</strong> {pickupLocation || 'Ikke angivet'}
          <br />
          <span style={{ color: '#666' }}>
            {cargo.pickup.date} {cargo.pickup.time}
          </span>
        </Typography>

        {deliveryLocation && (
          <Typography variant="body2">
            <strong>Levering:</strong> {deliveryLocation}
            <br />
            <span style={{ color: '#666' }}>
              {cargo.delivery?.date} {cargo.delivery?.time}
            </span>
          </Typography>
        )}

        {cargo.invoiceRef && (
          <Typography variant="body2">
            <strong>Faktura ref:</strong> {cargo.invoiceRef}
          </Typography>
        )}

        {cargo.adr && (
          <Chip
            label={cargo.adrText || 'ADR'}
            size="small"
            color="error"
            sx={{ mt: 0.5, width: 'fit-content' }}
          />
        )}

        {cargo.note && (
          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: '#666' }}>
            {cargo.note}
          </Typography>
        )}

        {cargo.qualities.length > 0 && (
          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {cargo.qualities.map((q) => (
              <Chip key={q} label={q} size="small" variant="outlined" />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function CargoBlock({ cargo, customColors }: { cargo: CalendarCargoEntry; customColors?: Record<string, string> }) {
  const color = getCargoColor(cargo.type, customColors);
  const pickupCity = cargo.pickup.city || 'Ukendt';
  const deliveryCity = cargo.delivery?.city;

  return (
    <Tooltip
      title={<CargoTooltipContent cargo={cargo} />}
      arrow
      placement="top"
      enterDelay={200}
    >
      <Box
        sx={{
          backgroundColor: color,
          color: 'white',
          borderRadius: 0.5,
          px: 0.5,
          py: 0.25,
          fontSize: '0.7rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          cursor: 'pointer',
          mb: 0.25,
          '&:hover': {
            opacity: 0.9,
            transform: 'scale(1.02)',
          },
          transition: 'all 0.15s ease',
        }}
      >
        {pickupCity}{deliveryCity ? ` → ${deliveryCity}` : ''}
      </Box>
    </Tooltip>
  );
}

export default function CargoCalendar({
  trailers,
  teams,
  isLoading,
  error,
  periodDays,
  onPeriodChange,
  startDate,
  cargoTypeColors,
  onUpdateColors,
  onResetColors,
  isUpdatingColors,
}: CargoCalendarProps) {
  const [filters, setFilters] = useState<FilterState>(loadFilters);
  const [filterAnchor, setFilterAnchor] = useState<HTMLButtonElement | null>(null);
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [editingColors, setEditingColors] = useState<Record<string, string>>({});
  const [selectedType, setSelectedType] = useState<string | null>(null);

  // Save filters to localStorage when they change
  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  // Generate array of dates for the period
  const dates = useMemo(() => {
    const result: Date[] = [];
    for (let i = 0; i < periodDays; i++) {
      result.push(addDays(startOfDay(startDate), i));
    }
    return result;
  }, [startDate, periodDays]);

  // Filter trailers based on hidden trailers and teams
  const filteredTrailers = useMemo(() => {
    return trailers.filter(trailer => {
      // Check if trailer is directly hidden
      if (filters.hiddenTrailers.includes(trailer.trailerId)) {
        return false;
      }
      // Check if all of trailer's teams are hidden
      if (trailer.teams.length > 0 && trailer.teams.every(teamId => filters.hiddenTeams.includes(teamId))) {
        return false;
      }
      return true;
    });
  }, [trailers, filters]);

  // For 7 days: use flex to fill available space, otherwise use fixed widths
  const useFlexColumns = periodDays <= 7;
  const dayColumnWidth = periodDays <= 14 ? 100 : 80;

  const handleToggleTrailer = (trailerId: string) => {
    setFilters(prev => {
      const isHidden = prev.hiddenTrailers.includes(trailerId);
      return {
        ...prev,
        hiddenTrailers: isHidden
          ? prev.hiddenTrailers.filter(id => id !== trailerId)
          : [...prev.hiddenTrailers, trailerId],
      };
    });
  };

  const handleToggleTeam = (teamId: string) => {
    setFilters(prev => {
      const isHidden = prev.hiddenTeams.includes(teamId);
      return {
        ...prev,
        hiddenTeams: isHidden
          ? prev.hiddenTeams.filter(id => id !== teamId)
          : [...prev.hiddenTeams, teamId],
      };
    });
  };

  const handleShowAll = () => {
    setFilters({ hiddenTrailers: [], hiddenTeams: [] });
  };

  const handleHideAll = () => {
    setFilters({
      hiddenTrailers: trailers.map(t => t.trailerId),
      hiddenTeams: teams.map(t => t.id),
    });
  };

  // Get unique cargo types from data
  const uniqueCargoTypes = useMemo(() => {
    const types = new Set<string>();
    trailers.forEach(trailer => {
      trailer.days.forEach(day => {
        day.cargos.forEach(cargo => {
          if (cargo.type) types.add(cargo.type);
        });
      });
    });
    return Array.from(types).sort();
  }, [trailers]);

  // Color settings handlers
  const handleOpenColorDialog = () => {
    setEditingColors(cargoTypeColors || {});
    setColorDialogOpen(true);
  };

  const handleColorChange = (type: string, color: string) => {
    setEditingColors(prev => ({ ...prev, [type]: color }));
    setSelectedType(null);
  };

  const handleSaveColors = async () => {
    if (onUpdateColors) {
      await onUpdateColors(editingColors);
    }
    setColorDialogOpen(false);
  };

  const handleResetColors = async () => {
    if (onResetColors) {
      await onResetColors();
    }
    setEditingColors({});
  };

  const activeFilterCount = filters.hiddenTrailers.length + filters.hiddenTeams.length;

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Kunne ikke hente kalenderdata: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Period selector and filter */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Cargo Kalender
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {onUpdateColors && (
            <Tooltip title="Farveindstillinger">
              <IconButton onClick={handleOpenColorDialog}>
                <PaletteIcon />
              </IconButton>
            </Tooltip>
          )}
          <Badge badgeContent={activeFilterCount} color="primary">
            <IconButton
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              color={activeFilterCount > 0 ? 'primary' : 'default'}
            >
              <FilterListIcon />
            </IconButton>
          </Badge>
          <ToggleButtonGroup
            value={periodDays}
            exclusive
            onChange={(_, value) => value && onPeriodChange(value)}
            size="small"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Filter popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 300, maxHeight: 500, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Filtre
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" onClick={handleShowAll}>
                Vis alle
              </Button>
              <Button size="small" onClick={handleHideAll}>
                Skjul alle
              </Button>
            </Box>
          </Box>

          {/* Teams filter */}
          {teams.length > 0 && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={teams.every(t => !filters.hiddenTeams.includes(t.id))}
                    indeterminate={
                      teams.some(t => !filters.hiddenTeams.includes(t.id)) &&
                      teams.some(t => filters.hiddenTeams.includes(t.id))
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Show all teams
                        setFilters(prev => ({
                          ...prev,
                          hiddenTeams: [],
                        }));
                      } else {
                        // Hide all teams
                        setFilters(prev => ({
                          ...prev,
                          hiddenTeams: teams.map(t => t.id),
                        }));
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="subtitle2" color="text.secondary">
                    Teams ({teams.length})
                  </Typography>
                }
                sx={{ mt: 1, mb: 0 }}
              />
              <FormGroup sx={{ pl: 2 }}>
                {teams.map(team => (
                  <FormControlLabel
                    key={team.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={!filters.hiddenTeams.includes(team.id)}
                        onChange={() => handleToggleTeam(team.id)}
                      />
                    }
                    label={<Typography variant="body2">{team.name}</Typography>}
                  />
                ))}
              </FormGroup>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Trailers filter */}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={trailers.every(t => !filters.hiddenTrailers.includes(t.trailerId))}
                indeterminate={
                  trailers.some(t => !filters.hiddenTrailers.includes(t.trailerId)) &&
                  trailers.some(t => filters.hiddenTrailers.includes(t.trailerId))
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    // Show all trailers
                    setFilters(prev => ({
                      ...prev,
                      hiddenTrailers: [],
                    }));
                  } else {
                    // Hide all trailers
                    setFilters(prev => ({
                      ...prev,
                      hiddenTrailers: trailers.map(t => t.trailerId),
                    }));
                  }
                }}
              />
            }
            label={
              <Typography variant="subtitle2" color="text.secondary">
                Køretøjer ({trailers.length})
              </Typography>
            }
            sx={{ mb: 0 }}
          />
          <FormGroup sx={{ pl: 2 }}>
            {trailers.map(trailer => {
              const trailerTeams = teams.filter(t => trailer.teams.includes(t.id));
              const displayName = trailer.trailerAlias
                ? `${trailer.trailerNumber} (${trailer.trailerAlias})`
                : trailer.trailerNumber;
              return (
                <FormControlLabel
                  key={trailer.trailerId}
                  control={
                    <Checkbox
                      size="small"
                      checked={!filters.hiddenTrailers.includes(trailer.trailerId)}
                      onChange={() => handleToggleTrailer(trailer.trailerId)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {displayName}
                      </Typography>
                      {trailerTeams.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {trailerTeams.map(t => t.name).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              );
            })}
          </FormGroup>
        </Box>
      </Popover>

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={60} />
          ))}
        </Box>
      ) : filteredTrailers.length === 0 ? (
        <Alert severity="info">
          Ingen køretøjer at vise. {activeFilterCount > 0 && 'Prøv at justere dine filtre.'}
        </Alert>
      ) : (
        <Paper
          sx={{
            overflow: 'auto',
            maxHeight: 'calc(100vh - 350px)',
          }}
        >
          <Box sx={{ display: 'flex', minWidth: 'fit-content' }}>
            {/* Trailer column header */}
            <Box
              sx={{
                minWidth: 120,
                maxWidth: 120,
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.100',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                }}
              >
                <Typography variant="subtitle2">Bil ({filteredTrailers.length})</Typography>
              </Box>

              {/* Trailer rows */}
              {filteredTrailers.map((trailer) => {
                // Calculate max cargos for this trailer on any day
                const maxCargos = Math.max(1, ...trailer.days.map(d => d.cargos.length));
                // Each cargo block is ~22px (py: 0.25 = 2px, text ~16px, mb: 0.25 = 2px, padding ~2px)
                const rowHeight = Math.max(60, maxCargos * 22 + 8);

                return (
                <Box
                  key={trailer.trailerId}
                  sx={{
                    minHeight: rowHeight,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    {trailer.color && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: trailer.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {trailer.trailerNumber}
                      </Typography>
                      {trailer.trailerAlias && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {trailer.trailerAlias}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
              })}
            </Box>

            {/* Date columns */}
            <Box sx={{ display: 'flex', flex: 1 }}>
              {dates.map((date) => {
                const dateStr = format(date, 'yyyy-MM-dd');
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;

                return (
                  <Box
                    key={dateStr}
                    sx={{
                      ...(useFlexColumns
                        ? { flex: 1, minWidth: 100 }
                        : { minWidth: dayColumnWidth, maxWidth: dayColumnWidth }),
                      borderRight: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {/* Date header */}
                    <Box
                      sx={{
                        height: 48,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        backgroundColor: isToday ? 'primary.light' : isWeekend ? 'grey.200' : 'grey.100',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: isToday ? 'primary.contrastText' : 'inherit' }}
                      >
                        {format(date, 'EEE', { locale: da })}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={isToday ? 'bold' : 'normal'}
                        sx={{ color: isToday ? 'primary.contrastText' : 'inherit' }}
                      >
                        {format(date, 'd/M')}
                      </Typography>
                    </Box>

                    {/* Cargo cells for each trailer */}
                    {filteredTrailers.map((trailer) => {
                      const dayData = trailer.days.find((d) => d.date === dateStr);
                      const cargos = dayData?.cargos || [];
                      // Calculate max cargos for this trailer on any day (same as trailer column)
                      const maxCargos = Math.max(1, ...trailer.days.map(d => d.cargos.length));
                      const rowHeight = Math.max(60, maxCargos * 22 + 8);

                      return (
                        <Box
                          key={`${trailer.trailerId}-${dateStr}`}
                          sx={{
                            minHeight: rowHeight,
                            p: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: isWeekend ? 'grey.50' : 'background.paper',
                          }}
                        >
                          {cargos.map((cargo) => (
                            <CargoBlock key={cargo.id} cargo={cargo} customColors={cargoTypeColors} />
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Legend - show types that are actually used */}
      {uniqueCargoTypes.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Typer:
          </Typography>
          {uniqueCargoTypes.slice(0, 8).map((type) => (
            <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  backgroundColor: getCargoColor(type, cargoTypeColors),
                }}
              />
              <Typography variant="caption">{formatCargoType(type)}</Typography>
            </Box>
          ))}
          {uniqueCargoTypes.length > 8 && (
            <Typography variant="caption" color="text.secondary">
              +{uniqueCargoTypes.length - 8} mere
            </Typography>
          )}
        </Box>
      )}

      {/* Color Settings Dialog */}
      <Dialog
        open={colorDialogOpen}
        onClose={() => setColorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Farveindstillinger for godstyper
            {onResetColors && (
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={handleResetColors}
                disabled={isUpdatingColors}
              >
                Nulstil
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Klik på farven for at ændre den. Du kan bruge samme farve til flere godstyper.
          </Typography>
          <List dense>
            {uniqueCargoTypes.map((type) => {
              const currentColor = editingColors[type] || getCargoColor(type, cargoTypeColors);
              return (
                <ListItem
                  key={type}
                  sx={{ py: 0.5 }}
                  secondaryAction={
                    selectedType === type ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                        {COLOR_PALETTE.map((color) => (
                          <Box
                            key={color}
                            onClick={() => handleColorChange(type, color)}
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 0.5,
                              backgroundColor: color,
                              cursor: 'pointer',
                              border: currentColor === color ? '2px solid #000' : '1px solid #ddd',
                              '&:hover': { transform: 'scale(1.1)' },
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Box
                        onClick={() => setSelectedType(type)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 0.5,
                          backgroundColor: currentColor,
                          cursor: 'pointer',
                          border: '1px solid #ddd',
                          '&:hover': { opacity: 0.8 },
                        }}
                      />
                    )
                  }
                >
                  <ListItemText primary={formatCargoType(type)} secondary={type} />
                </ListItem>
              );
            })}
          </List>
          {uniqueCargoTypes.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Ingen godstyper fundet i den valgte periode
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColorDialogOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={handleSaveColors}
            disabled={isUpdatingColors}
            startIcon={isUpdatingColors ? <CircularProgress size={16} /> : null}
          >
            Gem
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
